import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import './Admin.css';
import { CreditCardIcon, BankIcon, DollarIcon } from '../../components/SvgIcons';
import AedPrice from '../../components/AedPrice';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(() => {
    try { return localStorage.getItem('orders_filter') || 'all'; } catch { return 'all'; }
  });
  const [cityFilter, setCityFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showStatusDropdown, setShowStatusDropdown] = useState(window.innerWidth >= 768);
  const [openStatusMenu, setOpenStatusMenu] = useState(null);
  const [editingName, setEditingName] = useState(null);
  const [editNameValue, setEditNameValue] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const statusMenuRef = useRef(null);
  const nameInputRef = useRef(null);
  const { token } = useAuth();
  const { t, isRTL } = useLanguage();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusMenuRef.current && !statusMenuRef.current.contains(event.target)) {
        setOpenStatusMenu(null);
      }
    };

    if (openStatusMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openStatusMenu]);

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    try { localStorage.setItem('orders_filter', statusFilter); } catch {}
  }, [statusFilter]);

  const fetchOrders = async () => {
    try {
      const response = await fetch(`/api/orders`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setOrders(data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const response = await fetch(`/api/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus, sendEmail: false })
      });

      if (response.ok) {
        setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
        setOpenStatusMenu(null);
      }
    } catch (error) {
      console.error('Failed to update order status:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchOrders();
    setRefreshing(false);
  };

  const toggleStatusMenu = (orderId) => {
    setOpenStatusMenu(openStatusMenu === orderId ? null : orderId);
  };

  const startEditName = (orderId, currentName, e) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingName(orderId);
    setEditNameValue(currentName || '');
    setTimeout(() => nameInputRef.current?.focus(), 50);
  };

  const saveCustomerName = async (orderId, e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (!editNameValue.trim()) { setEditingName(null); return; }
    try {
      const response = await fetch(`/api/orders/${orderId}/customer`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ customerName: editNameValue.trim() })
      });
      if (response.ok) {
        setOrders(prev => prev.map(o => o.id === orderId ? { ...o, customerName: editNameValue.trim() } : o));
      }
    } catch (err) {
      console.error('Failed to update name:', err);
    }
    setEditingName(null);
  };

  const getEmirateShort = (emirate) => {
    if (!emirate) return '-';
    // Convert abu_dhabi to Abu Dhabi, etc.
    return emirate.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const CITY_COLORS = {
    'abu_dhabi':      { bg: '#e3f2fd', color: '#1565c0' },
    'dubai':          { bg: '#fce4ec', color: '#c62828' },
    'sharjah':        { bg: '#e8f5e9', color: '#2e7d32' },
    'al_ain':         { bg: '#fff3e0', color: '#e65100' },
    'ajman':          { bg: '#f3e5f5', color: '#7b1fa2' },
    'ras_al_khaimah': { bg: '#e0f7fa', color: '#00695c' },
    'fujairah':       { bg: '#fbe9e7', color: '#bf360c' },
    'umm_al_quwain':  { bg: '#e8eaf6', color: '#283593' }
  };

  const getCityStyle = (emirate) => {
    const key = (emirate || '').toLowerCase().replace(/[\s-]+/g, '_');
    return CITY_COLORS[key] || { bg: '#f3f4f6', color: '#374151' };
  };

  const formatDate = (dateString) => {
    const locale = isRTL ? 'ar-AE' : 'en-US';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => <AedPrice amount={amount} />;

  const getStatusBadge = (status) => {
    const statusMap = {
      created: 'created',
      pending: 'warning',
      processing: 'primary',
      shipped: 'shipped',
      delivered: 'success',
      cancelled: 'danger'
    };
    return statusMap[status] || 'secondary';
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      all: t('all'),
      created: isRTL ? 'تم الإنشاء' : 'Created',
      pending: t('pending'),
      processing: t('processing'),
      shipped: t('shipped'),
      delivered: t('delivered'),
      cancelled: t('cancelled')
    };
    return statusLabels[status] || status;
  };

  const getPaymentInfo = (order) => {
    const method = order.paymentMethod || 'cod';
    const status = order.paymentStatus || 'pending';

    if (method === 'card') {
      let statusLabel = 'Pending';
      let className = 'capsule-payment-card-pending';

      if (status === 'paid') {
        statusLabel = 'Paid';
        className = 'capsule-payment-card-paid';
      } else if (status === 'refunded') {
        statusLabel = 'Refunded';
        className = 'capsule-payment-refunded';
      } else if (status === 'partially_refunded') {
        statusLabel = 'Partial Refund';
        className = 'capsule-payment-refunded';
      } else if (status === 'failed') {
        statusLabel = 'Failed';
        className = 'capsule-payment-failed';
      }

      return {
        icon: <CreditCardIcon size="16px" />,
        label: 'Card',
        status: statusLabel,
        className: className
      };
    } else if (method === 'bank_transfer') {
      let statusLabel = 'Pending';
      let className = 'capsule-payment-bank-pending';

      if (status === 'paid' || status === 'verified') {
        statusLabel = 'Verified';
        className = 'capsule-payment-bank-verified';
      } else if (status === 'pending_verification') {
        statusLabel = 'Verify';
        className = 'capsule-payment-bank-pending';
      }

      return {
        icon: <BankIcon size="16px" />,
        label: 'Bank',
        status: statusLabel,
        className: className
      };
    } else {
      return {
        icon: <DollarIcon size="16px" />,
        label: 'COD',
        status: status === 'paid' ? 'Paid' : 'Pending',
        className: status === 'paid' ? 'capsule-payment-cod-paid' : 'capsule-payment-cod-pending'
      };
    }
  };

  const statuses = ['all', 'created', 'pending', 'processing', 'shipped', 'delivered', 'cancelled'];

  // Get unique cities from orders for filter (normalize to lowercase for dedup)
  const uniqueCities = (() => {
    const cityMap = {};
    orders.forEach(o => {
      const raw = (o.shippingAddress?.emirate || '').trim();
      if (!raw) return;
      const key = raw.toLowerCase().replace(/[\s_-]+/g, '_');
      if (!cityMap[key]) cityMap[key] = { key, raw, count: 0 };
      cityMap[key].count++;
    });
    return Object.values(cityMap).sort((a, b) => b.count - a.count);
  })();

  // Count orders per status (from all orders)
  const statusCounts = {};
  orders.forEach(order => {
    statusCounts[order.status] = (statusCounts[order.status] || 0) + 1;
  });
  statusCounts.all = orders.length;

  // Filter orders by status, search query, and city filter
  const filteredOrders = orders.filter(order => {
    // Status filter
    if (statusFilter !== 'all' && order.status !== statusFilter) return false;
    // City filter
    if (cityFilter !== 'all') {
      const orderCity = (order.shippingAddress?.emirate || '').trim().toLowerCase().replace(/[\s_-]+/g, '_');
      if (orderCity !== cityFilter) return false;
    }
    // Search filter
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const orderId = (order.id || '').toLowerCase();
    const name = (order.customerName || order.shippingAddress?.fullName || '').toLowerCase();
    const waName = (order.whatsappName || '').toLowerCase();
    const phone = (order.customerPhone || order.shippingAddress?.phone || '').toLowerCase();
    const emirate = (order.shippingAddress?.emirate || '').toLowerCase().replace('_', ' ');
    return orderId.includes(query) || name.includes(query) || waName.includes(query) || phone.includes(query) || emirate.includes(query);
  });

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`admin-page ${isRTL ? 'rtl' : ''}`}>
      <div className="container">

        <div className="admin-card" style={{ padding: '12px 16px', marginBottom: '24px', ...(window.innerWidth >= 768 ? { position: 'sticky', top: '72px', zIndex: 99 } : {}) }}>
          <div className="admin-nav" style={{ marginBottom: 0 }}>
            <Link to="/dashboard4">{t('dashboard')}</Link>
            <Link to="/dashboard4/products">{t('products')}</Link>
            <Link to="/dashboard4/orders" className="active">{t('orders')}</Link>
            <Link to="/dashboard4/create-order">{isRTL ? 'إنشاء طلب' : 'Create Order'}</Link>
            <Link to="/dashboard4/inventory">{isRTL ? "المخزون" : "Inventory"}</Link>
            <Link to="/dashboard4/stats">{isRTL ? "الإحصائيات" : "Stats"}</Link>
            <Link to="/dashboard4/settings">{isRTL ? 'الإعدادات' : 'Settings'}</Link>
          </div>
        </div>

        {/* Search & Filter Bar */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          border: '1px solid #e5e7eb',
          marginBottom: '20px',
          overflow: 'hidden'
        }}>
          {/* Search Row */}
          <div style={{ display: 'flex', gap: '8px', padding: '12px 16px', alignItems: 'center' }}>
            <div className="search-box" style={{ flex: 1 }}>
              <span className="search-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              </span>
              <input
                type="text"
                placeholder={isRTL ? 'بحث بالاسم، الهاتف، رقم الطلب...' : 'Search by name, phone, order ID...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{
                    position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                    background: '#e5e7eb', border: 'none', borderRadius: '50%',
                    width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', fontSize: '11px', color: '#6b7280', fontWeight: '700'
                  }}
                >✕</button>
              )}
            </div>

            {/* Refresh Button */}
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: '42px', height: '42px', borderRadius: '12px',
                border: '1px solid #e5e7eb', background: 'white',
                cursor: refreshing ? 'wait' : 'pointer',
                transition: 'all 0.2s', flexShrink: 0
              }}
              title={isRTL ? 'تحديث' : 'Refresh'}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={refreshing ? '#9ca3af' : '#374151'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ animation: refreshing ? 'spin 0.8s linear infinite' : 'none' }}
              >
                <polyline points="23 4 23 10 17 10"/>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
              </svg>
            </button>

            {/* Filter Toggle - mobile only */}
            <button
              onClick={() => setShowStatusDropdown(!showStatusDropdown)}
              style={{
                display: window.innerWidth >= 768 ? 'none' : 'flex',
                alignItems: 'center', justifyContent: 'center',
                width: '42px', height: '42px', borderRadius: '12px',
                border: showStatusDropdown ? '1.5px solid var(--primary)' : '1px solid #e5e7eb',
                background: showStatusDropdown ? '#eff6ff' : 'white',
                cursor: 'pointer', flexShrink: 0
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={showStatusDropdown ? 'var(--primary)' : '#374151'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
              </svg>
            </button>
          </div>

          {/* Filters Area */}
          {showStatusDropdown && (
            <div style={{ borderTop: '1px solid #f1f5f9' }}>
              {/* Status Filters */}
              <div style={{
                display: 'flex', gap: '8px', padding: '12px 16px 8px',
                overflowX: 'auto', WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'none', msOverflowStyle: 'none'
              }}>
                {statuses.map(status => {
                  const isActive = statusFilter === status;
                  const count = statusCounts[status] || 0;
                  const STATUS_STYLE = {
                    all:        { color: '#3478f6', bg: '#eff6ff' },
                    created:    { color: '#6b7280', bg: '#f3f4f6' },
                    pending:    { color: '#d97706', bg: '#fffbeb' },
                    processing: { color: '#3478f6', bg: '#eff6ff' },
                    shipped:    { color: '#7c3aed', bg: '#f5f3ff' },
                    delivered:  { color: '#059669', bg: '#ecfdf5' },
                    cancelled:  { color: '#dc2626', bg: '#fef2f2' }
                  };
                  const st = STATUS_STYLE[status] || STATUS_STYLE.all;
                  const iconColor = isActive ? 'white' : st.color;
                  const STATUS_ICONS = {
                    all: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>,
                    created: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
                    pending: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
                    processing: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
                    shipped: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>,
                    delivered: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
                    cancelled: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                  };
                  return (
                    <button
                      key={status}
                      onClick={() => setStatusFilter(status)}
                      style={{
                        flexShrink: 0,
                        display: 'flex', alignItems: 'center', gap: '6px',
                        padding: '8px 14px', borderRadius: '12px',
                        border: isActive ? `2px solid ${st.color}` : '1.5px solid #e5e7eb',
                        background: isActive ? st.color : 'white',
                        color: isActive ? 'white' : '#374151',
                        cursor: 'pointer', transition: 'all 0.15s',
                        fontSize: '13px', fontWeight: '700'
                      }}
                    >
                      {STATUS_ICONS[status]}
                      <span>{getStatusLabel(status)}</span>
                      {count > 0 && (
                        <span style={{
                          background: isActive ? 'rgba(255,255,255,0.25)' : st.bg,
                          color: isActive ? 'white' : st.color,
                          padding: '1px 8px', borderRadius: '8px',
                          fontSize: '12px', fontWeight: '800'
                        }}>
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* City Filter */}
              {uniqueCities.length > 0 && (
                <div style={{
                  display: 'flex', gap: '8px', padding: '8px 16px 12px',
                  overflowX: 'auto', WebkitOverflowScrolling: 'touch',
                  scrollbarWidth: 'none', msOverflowStyle: 'none'
                }}>
                  <button
                    onClick={() => setCityFilter('all')}
                    style={{
                      flexShrink: 0,
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '8px 14px', borderRadius: '12px',
                      border: cityFilter === 'all' ? '2px solid #3478f6' : '1.5px solid #e5e7eb',
                      background: cityFilter === 'all' ? '#3478f6' : 'white',
                      color: cityFilter === 'all' ? 'white' : '#374151',
                      cursor: 'pointer', transition: 'all 0.15s',
                      fontSize: '13px', fontWeight: '700'
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={cityFilter === 'all' ? 'white' : '#3478f6'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                    <span>{isRTL ? 'الكل' : 'All'}</span>
                    <span style={{
                      background: cityFilter === 'all' ? 'rgba(255,255,255,0.2)' : '#f1f5f9',
                      padding: '1px 8px', borderRadius: '8px',
                      fontSize: '12px', fontWeight: '800'
                    }}>{orders.length}</span>
                  </button>
                  {uniqueCities.map(city => {
                    const cs = getCityStyle(city.key);
                    const isActive = cityFilter === city.key;
                    return (
                      <button
                        key={city.key}
                        onClick={() => setCityFilter(city.key)}
                        style={{
                          flexShrink: 0,
                          display: 'flex', alignItems: 'center', gap: '6px',
                          padding: '8px 14px', borderRadius: '12px',
                          border: isActive ? `2px solid ${cs.color}` : '1.5px solid #e5e7eb',
                          background: isActive ? cs.color : 'white',
                          color: isActive ? 'white' : '#374151',
                          cursor: 'pointer', transition: 'all 0.15s',
                          fontSize: '13px', fontWeight: '700'
                        }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={isActive ? 'white' : cs.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                          <circle cx="12" cy="10" r="3"/>
                        </svg>
                        <span>{getEmirateShort(city.key)}</span>
                        <span style={{
                          background: isActive ? 'rgba(255,255,255,0.25)' : cs.bg,
                          color: isActive ? 'white' : cs.color,
                          padding: '1px 8px', borderRadius: '8px',
                          fontSize: '12px', fontWeight: '800'
                        }}>{city.count}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Results count */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', padding: '0 4px' }}>
          <span style={{ fontSize: '13px', color: '#9ca3af', fontWeight: '500' }}>
            {filteredOrders.length === orders.length
              ? (isRTL ? `${orders.length} طلب` : `${orders.length} orders`)
              : (isRTL ? `${filteredOrders.length} من ${orders.length} طلب` : `${filteredOrders.length} of ${orders.length} orders`)
            }
          </span>
          {(statusFilter !== 'all' || cityFilter !== 'all' || searchQuery) && (
            <button
              onClick={() => { setStatusFilter('all'); setCityFilter('all'); setSearchQuery(''); }}
              style={{
                fontSize: '12px', color: '#ef4444', fontWeight: '600',
                background: 'none', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '4px'
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              {isRTL ? 'مسح الفلاتر' : 'Clear filters'}
            </button>
          )}
        </div>

        {filteredOrders.length === 0 ? (
          <div className="admin-card">
            <p className="empty-text">{t('noOrdersFound')}</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="products-table desktop-only">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{t('orderId')}</th>
                    <th>{t('customer')}</th>
                    <th>Emirate / City</th>
                    <th>{t('date')}</th>
                    <th>{t('items')}</th>
                    <th>{t('total')}</th>
                    <th>Payment</th>
                    <th>{t('status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order, idx) => (
                    <tr key={order.id} onClick={() => window.location.href = `/dashboard4/orders/${order.id}`} style={{ cursor: 'pointer' }}>
                      <td style={{ color: '#9ca3af', fontSize: '15px', fontWeight: '600' }}>{idx + 1}</td>
                      <td>
                        <Link to={`/dashboard4/orders/${order.id}`} style={{ color: 'var(--primary)' }} onClick={(e) => e.stopPropagation()}>
                          #{order.id}
                        </Link>
                        {order.printed ? <div style={{ fontSize: '10px', color: '#b45309', fontWeight: '600', marginTop: '2px' }}>🖨 Printed</div> : null}
                      </td>
                      <td>
                        <div>
                          {editingName === order.id ? (
                            <form onSubmit={(e) => saveCustomerName(order.id, e)} style={{ display: 'inline' }} onClick={(e) => e.stopPropagation()}>
                              <input
                                ref={nameInputRef}
                                type="text"
                                value={editNameValue}
                                onChange={(e) => setEditNameValue(e.target.value)}
                                onBlur={() => saveCustomerName(order.id)}
                                onKeyDown={(e) => { if (e.key === 'Escape') setEditingName(null); }}
                                style={{ fontSize: '14px', fontWeight: '600', padding: '2px 6px', border: '1.5px solid #3478f6', borderRadius: '6px', outline: 'none', width: '160px' }}
                              />
                            </form>
                          ) : (
                            <strong
                              onClick={(e) => startEditName(order.id, order.customerName, e)}
                              style={{ cursor: 'text', borderBottom: '1px dashed #d1d5db', paddingBottom: '1px' }}
                              title={isRTL ? 'اضغط للتعديل' : 'Click to edit'}
                            >
                              {order.customerName || '-'}
                            </strong>
                          )}
                          {order.whatsappName && order.whatsappName !== order.customerName && (
                            <span style={{ color: '#25D366', fontSize: '12px', marginLeft: '6px', fontWeight: '500' }}>({order.whatsappName})</span>
                          )}
                          <br />
                          <span style={{ color: '#000', fontFamily: 'monospace', fontSize: '14px' }}>{order.customerPhone || order.shippingAddress?.phone || '-'}</span>
                        </div>
                      </td>
                      <td><span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', background: getCityStyle(order.shippingAddress?.emirate).bg, color: getCityStyle(order.shippingAddress?.emirate).color }}>{getEmirateShort(order.shippingAddress?.emirate)}</span></td>
                      <td>{formatDate(order.createdAt)}</td>
                      <td>{order.items.length}</td>
                      <td>{formatCurrency(order.total)}</td>
                      <td>
                        {(() => {
                          const payment = getPaymentInfo(order);
                          return (
                            <span className={`capsule capsule-payment ${payment.className}`}>
                              {payment.icon} {payment.label} ({payment.status})
                            </span>
                          );
                        })()}
                      </td>
                      <td>
                        <div className="order-status-wrapper" ref={openStatusMenu === order.id ? statusMenuRef : null}>
                          <span
                            className={`capsule capsule-status capsule-${getStatusBadge(order.status)}`}
                            onClick={(e) => { e.stopPropagation(); toggleStatusMenu(order.id); }}
                          >
                            {getStatusLabel(order.status)} ▼
                          </span>

                          {openStatusMenu === order.id && (
                            <div className="status-dropdown">
                              {['created', 'pending', 'processing', 'shipped', 'delivered', 'cancelled'].map(status => (
                                <div
                                  key={status}
                                  className={`status-option status-${getStatusBadge(status)} ${order.status === status ? 'active' : ''}`}
                                  onClick={(e) => { e.stopPropagation(); updateOrderStatus(order.id, status); }}
                                >
                                  {getStatusLabel(status)}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="orders-mobile mobile-only">
              {filteredOrders.map((order, idx) => (
                <div key={order.id} className="order-card-mobile">
                  <Link to={`/dashboard4/orders/${order.id}`} className="order-card-link">
                    <div className="order-card-header">
                      <span className="order-id-link" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>
                          <span style={{ color: '#9ca3af', fontSize: '15px', marginRight: '4px' }}>{idx + 1}.</span>
                          #{order.id}
                        </span>
                        {order.printed ? <span style={{ fontSize: '9px', color: '#b45309', fontWeight: '600', background: '#fffbeb', padding: '1px 6px', borderRadius: '8px' }}>🖨 Printed</span> : null}
                      </span>
                      <span className="order-date">{formatDate(order.createdAt)}</span>
                    </div>

                    <div className="order-customer-row">
                      <div>
                        {editingName === order.id ? (
                          <form onSubmit={(e) => saveCustomerName(order.id, e)} onClick={(e) => e.preventDefault()}>
                            <input
                              ref={nameInputRef}
                              type="text"
                              value={editNameValue}
                              onChange={(e) => setEditNameValue(e.target.value)}
                              onBlur={() => saveCustomerName(order.id)}
                              onKeyDown={(e) => { if (e.key === 'Escape') setEditingName(null); }}
                              onClick={(e) => e.preventDefault()}
                              style={{ fontSize: '14px', fontWeight: '600', padding: '4px 8px', border: '1.5px solid #3478f6', borderRadius: '6px', outline: 'none', width: '180px' }}
                            />
                          </form>
                        ) : (
                          <strong
                            onClick={(e) => startEditName(order.id, order.customerName, e)}
                            style={{ cursor: 'text', borderBottom: '1px dashed #d1d5db', paddingBottom: '1px' }}
                          >
                            {order.customerName || '-'}
                          </strong>
                        )}
                        {order.whatsappName && order.whatsappName !== order.customerName && (
                          <span style={{ color: '#25D366', fontSize: '12px', marginLeft: '6px', fontWeight: '500' }}>({order.whatsappName})</span>
                        )}
                        <div style={{ fontSize: '14px', color: '#000', fontFamily: 'monospace', marginTop: '2px' }}>
                          {order.customerPhone || order.shippingAddress?.phone || '-'}
                        </div>
                      </div>
                      <div className="order-status-wrapper" ref={openStatusMenu === order.id ? statusMenuRef : null} onClick={(e) => e.preventDefault()}>
                        <span
                          className={`capsule capsule-status capsule-${getStatusBadge(order.status)}`}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleStatusMenu(order.id); }}
                        >
                          {getStatusLabel(order.status)} ▼
                        </span>

                        {openStatusMenu === order.id && (
                          <div className="status-dropdown">
                            {['created', 'pending', 'processing', 'shipped', 'delivered', 'cancelled'].map(status => (
                              <div
                                key={status}
                                className={`status-option status-${getStatusBadge(status)} ${order.status === status ? 'active' : ''}`}
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); updateOrderStatus(order.id, status); }}
                              >
                                {getStatusLabel(status)}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="order-capsules">
                      <span className="capsule" style={{ background: getCityStyle(order.shippingAddress?.emirate).bg, color: getCityStyle(order.shippingAddress?.emirate).color }}>
                        {getEmirateShort(order.shippingAddress?.emirate)}
                      </span>
                      <span className="capsule capsule-items">
                        {order.items.length} {order.items.length === 1 ? 'item' : 'items'}
                      </span>
                      <span className="capsule capsule-total">
                        {formatCurrency(order.total)}
                      </span>
                      {(() => {
                        const payment = getPaymentInfo(order);
                        return (
                          <span className={`capsule capsule-payment ${payment.className}`}>
                            {payment.icon} {payment.label} ({payment.status})
                          </span>
                        );
                      })()}
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Orders;
